import React, { useState, useEffect, useCallback } from 'react';
import { Sun, Moon, Monitor } from '@phosphor-icons/react';
import { useI18n } from '../../context/I18nContext';

function applyTheme(theme: 'light' | 'dark' | 'system') {
  const root = document.documentElement;
  if (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

function loadTheme(): 'light' | 'dark' | 'system' {
  return (localStorage.getItem('theme') as 'light' | 'dark' | 'system') || 'system';
}

export const ThemeToggle: React.FC = () => {
  const { t } = useI18n();
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(loadTheme);

  useEffect(() => {
    localStorage.setItem('theme', theme);
    applyTheme(theme);
  }, [theme]);

  // Sync if another tab changes the theme
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'theme' && e.newValue) {
        const next = e.newValue as 'light' | 'dark' | 'system';
        setTheme(next);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const toggleTheme = useCallback(() => {
    const themes: ('light' | 'dark' | 'system')[] = ['light', 'dark', 'system'];
    const next = themes[(themes.indexOf(theme) + 1) % themes.length];
    setTheme(next);
  }, [theme]);

  const icon = (() => {
    switch (theme) {
      case 'light': return <Sun size={20} weight="regular" />;
      case 'dark': return <Moon size={20} weight="regular" />;
      default: return <Monitor size={20} weight="regular" />;
    }
  })();

  return (
    <button
      onClick={toggleTheme}
      className="p-2.5 rounded-xl text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100 transition-all duration-150"
      aria-label={t('theme.toggle')}
    >
      {icon}
    </button>
  );
};
